import { ActionContext } from 'vuex';
import { VehicleDashboardState } from '@/store/modules/vehicleDashboard/vehicleDashboardState';
import { RootState } from '@/store/rootState';
import { Vehicle } from '@/core/interfaces/vehicle';
import { VehicleType } from '@/core/interfaces/vehicleType';
import axios from 'axios';
import { LelyAuth, RelayService, StringResponse, Subscription } from '@hn-work/frontend-vue-shared';
import { VehicleState } from '@/core/interfaces/vehicleState';
import store from '@/store';

let relayService: RelayService | null = null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const subscriptions: Record<string, Subscription<any>> = {};

let pingInterval: number;

const actions = {
    openRelay: (context: ActionContext<VehicleDashboardState, RootState>, relayServiceHostname: string) => {
        relayService = new RelayService(
            `wss://${relayServiceHostname}.next.lely.cloud/ws`,
            'machine',
            'hmf',
            () => String(LelyAuth.getAccessToken()),
            false,
            () => String(store.getters.currentUser.id),
            () => String(store.getters.currentUser.username),
        );

        clearInterval(pingInterval);
        pingInterval = setInterval(() => relayService?.ping(), 30 * 1000);
    },
    close: () => {
        clearInterval(pingInterval);
        relayService?.close();
        relayService = null;
    },
    reset: (context: ActionContext<VehicleDashboardState, RootState>) => {
        context.commit('setMachine', null);
        context.commit('setCamera', null);
        context.commit('setCameraTopic', null);
        context.commit('setStatus', null);
        context.commit('setStatusTopic', null);
        context.commit('setDefinition', null);
        context.commit('setPaused', false);
        context.commit('setLaserscan', null);
        context.commit('setFootprint', null);
        context.commit('setMaintenanceMode', false);
        context.commit('setMaintenanceModeUser', null);
        context.commit('setLogs', null);
        context.commit('setVehicleState', null);
        context.commit('clearRealtimeMapLayers');
    },
    initVehicle: async(context: ActionContext<VehicleDashboardState, RootState>, vehicle: Vehicle) => {
        await context.dispatch('openRelay', vehicle.relay_server_hostname);

        context.commit('setMachine', vehicle.name);

        const vehicleType = context.rootGetters.vehicleTypeById(vehicle.vehicle_type_id);

        context.commit('setVehicleType', vehicleType);
        await context.dispatch('loadDefinition', vehicleType);
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    loadDefinition: async(context: ActionContext<VehicleDashboardState, RootState>, vehicleType: VehicleType) => {
        const res = await axios.get(`/vehicle-types/${vehicleType.id}/dashboard-definition`);

        context.commit('setDefinition', res.data);
    },
    loadCamera: async(context: ActionContext<VehicleDashboardState, RootState>, topic: string) => {
        if (!context.state.machine) {
            return;
        }

        if (!relayService) {
            throw new Error('RelayService not initialized');
        }

        await context.dispatch('closeCamera');
        context.commit('setCameraTopic', topic);

        const subscription = await relayService.stringSubscribe(context.state.machine, topic);
        subscription.setCallback(response => {
            context.commit('setCamera', response.data);
        });

        subscriptions.camera = subscription;
    },
    loadStatus: async(context: ActionContext<VehicleDashboardState, RootState>, topic: string) => {
        if (!context.state.machine) {
            return;
        }

        if (!relayService) {
            throw new Error('RelayService not initialized');
        }

        await context.dispatch('closeStatus');
        context.commit('setStatusTopic', topic);

        const isStringResponse = (response: any): response is StringResponse => {
            return typeof response?.data === 'string';
        };

        const subscription = await relayService.subscribe<VehicleState | StringResponse>(context.state.machine, topic);
        subscription.setCallback(response => {
            context.commit('updateLastMessageReceived');

            if (isStringResponse(response)) {
                context.commit('setStatus', response.data);
            } else {
                context.commit('setVehicleState', response);
            }
        });

        subscriptions.status = subscription;
    },
    loadPaused: async(context: ActionContext<VehicleDashboardState, RootState>) => {
        if (!context.state.machine || !context.state.definition?.pause?.topic) {
            return;
        }

        if (!relayService) {
            throw new Error('RelayService not initialized');
        }

        const subscription = await relayService.boolSubscribe(context.state.machine, context.state.definition.pause.topic);
        subscription.setCallback(response => {
            context.commit('updateLastMessageReceived');
            context.commit('setPaused', response.data);
        });
    },
    loadMaintenanceMode: async(context: ActionContext<VehicleDashboardState, RootState>) => {
        if (!context.state.machine) {
            return;
        }

        if (!relayService) {
            throw new Error('RelayService not initialized');
        }

        if (context.state.definition?.maintenance_mode?.topic) {
            const subscription = await relayService.boolSubscribe(context.state.machine, context.state.definition.maintenance_mode.topic);
            subscription.setCallback(response => {
                context.commit('setMaintenanceMode', response.data);
            });
        } else if (context.state.definition?.maintenance_mode_username?.topic) {
            const subscription = await relayService.subscribe<{
                username: string;
                enabled: string;
            }>(context.state.machine, context.state.definition.maintenance_mode_username.topic);
            subscription.setCallback(response => {
                context.commit('setMaintenanceMode', response.enabled);
                context.commit('setMaintenanceModeUser', response.username);
            });
        } else if (context.state.definition?.maintenance_mode_user_id?.topic) {
            const subscription = await relayService.subscribe<{
                user_name: string;
                user_id: string;
                enabled: string;
            }>(context.state.machine, context.state.definition.maintenance_mode_user_id.topic);
            subscription.setCallback(response => {
                context.commit('setMaintenanceMode', response.enabled);
                context.commit('setMaintenanceModeUser', response.user_name);
            });
        }
    },
    loadSingleLaserscan: async(context: ActionContext<VehicleDashboardState, RootState>) => {
        if (!context.state.machine || !context.state.definition?.features?.laserscan_offset?.laserscan_topic) {
            return;
        }

        if (!relayService) {
            throw new Error('RelayService not initialized');
        }

        context.commit('setLaserscan', null);

        const subscription = await relayService.stringSubscribe(context.state.machine, context.state.definition?.features?.laserscan_offset?.laserscan_topic);
        subscription.setCallback(response => {
            const geojson = JSON.parse(response.data).geojson;
            context.commit('setLaserscan', geojson);
            relayService?.unsubscribe(subscription);
        });
    },
    loadSingleFootprint: async(context: ActionContext<VehicleDashboardState, RootState>) => {
        if (!context.state.machine || !context.state.definition?.features?.laserscan_offset?.footprint_topic) {
            return;
        }

        if (!relayService) {
            throw new Error('RelayService not initialized');
        }

        context.commit('setFootprint', null);

        const subscription = await relayService.stringSubscribe(context.state.machine, context.state.definition?.features?.laserscan_offset?.footprint_topic);
        subscription.setCallback(response => {
            const geojson = JSON.parse(response.data).geojson;
            context.commit('setFootprint', geojson);
            relayService?.unsubscribe(subscription);
        });
    },
    loadSingleRobotPosition: async(context: ActionContext<VehicleDashboardState, RootState>) => {
        if (!context.state.machine || !context.state.definition?.features?.laserscan_offset?.robotposition_topic) {
            return;
        }

        if (!relayService) {
            throw new Error('RelayService not initialized');
        }

        context.commit('setRobotPosition', null);

        const subscription = await relayService.subscribe<{ x: number; y: number }>(
            context.state.machine,
            context.state.definition?.features?.laserscan_offset?.robotposition_topic);

        subscription.setCallback(response => {
            context.commit('setRobotPosition', { x: response.x, y: response.y });
            relayService?.unsubscribe(subscription);
        });
    },
    loadRealtimeMapLayers: async(context: ActionContext<VehicleDashboardState, RootState>) => {
        if (!context.state.machine || !context.state.definition?.realtime_map_layers) {
            return;
        }

        const promisses = context.state.definition.realtime_map_layers.map(async(realtimeMapLayer) => {
            if (!relayService) {
                throw new Error('RelayService not initialized');
            }

            const subscription = await relayService.stringSubscribe(context.state.machine as string, realtimeMapLayer.topic);

            subscription.setCallback(data => {
                context.commit('updateLastMessageReceived');

                let geojson;

                try {
                    geojson = JSON.parse(data.data).geojson;

                    context.commit('setRealtimeMapLayer', {
                        topic: realtimeMapLayer.topic,
                        geojson,
                    });
                } catch (e) {
                    console.warn('Invalid JSON for realtimeMapLayer', data);
                }
            });
        });

        await Promise.all(promisses);
    },
    closeCamera: async(context: ActionContext<VehicleDashboardState, RootState>) => {
        if (!relayService) {
            throw new Error('RelayService not initialized');
        }

        context.commit('setCamera', null);
        context.commit('setCameraTopic', null);

        if (subscriptions.camera) {
            await relayService.unsubscribe(subscriptions.camera);
            delete subscriptions.camera;
        }
    },
    closeStatus: async(context: ActionContext<VehicleDashboardState, RootState>) => {
        if (!relayService) {
            throw new Error('RelayService not initialized');
        }

        context.commit('setStatusTopic', null);
        context.commit('setStatus', null);

        if (subscriptions.status) {
            await relayService.unsubscribe(subscriptions.status);
            delete subscriptions.status;
        }
    },
    callService: async(context: ActionContext<VehicleDashboardState, RootState>, payload: {
        service: string;
        args: Record<string, unknown>;
    }) => {
        if (!relayService) {
            throw new Error('RelayService not initialized');
        }

        if (!context.state.machine) {
            return;
        }

        return await relayService.callService(context.state.machine, payload.service, payload.args);
    },
    callPause: async(context: ActionContext<VehicleDashboardState, RootState>, payload: boolean) => {
        if (!context.state.machine || !context.state.definition?.pause?.service) {
            return;
        }

        if (!relayService) {
            throw new Error('RelayService not initialized');
        }

        await relayService.callService(context.state.machine, context.state.definition.pause.service, { data: payload });
    },
    callStop: async(context: ActionContext<VehicleDashboardState, RootState>) => {
        if (!context.state.machine || !context.state.definition?.stop?.service) {
            return;
        }

        if (!relayService) {
            throw new Error('RelayService not initialized');
        }

        await relayService.callService(context.state.machine, context.state.definition.stop.service);
    },
    callMaintenanceMode: async(context: ActionContext<VehicleDashboardState, RootState>, payload: boolean) => {
        if (!context.state.machine) {
            return;
        }

        if (!relayService) {
            throw new Error('RelayService not initialized');
        }

        if (context.state.definition?.maintenance_mode?.service) {
            await relayService.callService(context.state.machine, context.state.definition.maintenance_mode.service, { data: payload });
        } else if (context.state.definition?.maintenance_mode_username?.service) {
            await relayService.callService(context.state.machine, context.state.definition.maintenance_mode_username.service, {
                enable: payload,
                username: context.rootGetters.currentUser.username,
            });
        } else if (context.state.definition?.maintenance_mode_user_id?.service) {
            await relayService.callService(context.state.machine, context.state.definition.maintenance_mode_user_id.service, {
                enable: payload,
                user_name: context.rootGetters.currentUser.username,
                user_id: context.rootGetters.currentUser.id,
            });
        }
    },
    loadLogScreens: async(context: ActionContext<VehicleDashboardState, RootState>) => {
        if (!context.state.machine || !context.state.definition?.features?.logs?.screens_service) {
            return;
        }

        if (!relayService) {
            throw new Error('RelayService not initialized');
        }

        const result = await relayService.callServiceAndAwaitResult<any>(context.state.machine, context.state.definition.features.logs.screens_service);
        const screens = JSON.parse(result.data);

        context.commit('setLogScreens', screens);
    },
    loadLogs: async(context: ActionContext<VehicleDashboardState, RootState>, payload: {
        screen: string;
        numberOfLines: number;
    }) => {
        if (!context.state.machine || !context.state.definition?.features?.logs?.logs_service) {
            return;
        }

        if (!relayService) {
            throw new Error('RelayService not initialized');
        }

        const result = await relayService.callServiceAndAwaitResult<{
            data: string;
        }>(context.state.machine, context.state.definition.features.logs.logs_service, {
            id: payload.screen,
            number_of_lines: payload.numberOfLines,
        });

        context.commit('setLogs', result.data);
    },
};

export default actions;
